<template>
  <v-row class="login-row dashboard">
    <v-col cols="12" sm="6" class="text-center">
      <img
        :src="images.jaceyUwill"
        style="margin-top:44px;"
        alt="Uwill"
        :style="
          $vuetify.display.smAndUp ? 'max-width:560px;' : 'max-width:300px;'
        "
      />
    </v-col>
    <v-col
      cols="12"
      sm="6"
      order="first"
      order-sm="last"
      class="pt-5 login-box page-content"
    >
      <span class="dashboard-panel">
        <div
          class="details-container featured"
          style="max-width:500px;margin:auto auto;"
        >
          <div class="details-title">
            <h1 class="details-title-text">Log In to Uwill</h1>
          </div>
          <div v-if="gso_mode" class="details-content">
            <v-card flat>
              <div class="text-caption mb-3">
                You can not log in to Uwill this way. Please contact your school
                administrator for instructions on how to log in.
              </div>
            </v-card>
          </div>
          <div v-else-if="!use_code" class="details-content">
            <v-card v-if="!passwordStage" flat>
              <div v-if="sessionTimedOut" class="text-caption mb-3 text-red">
                For your security, your session has timed out. Please log in again.
              </div>
              <div class="text-caption mb-3">
                Please use your school or work email address if you are part of
                an organization.
              </div>
              <v-text-field
                id="email"
                ref="email"
                v-model="email"
                label="Email Address"
                class="ada-red login-fields"
                name="email"
                type="text"
                required
                :error-messages="emailErrors"
                autocomplete="email"
                @update:model-value="v$.email.$touch()"
                @blur="
                  v$.email.$touch();
                  email = email.trim();
                "
                @keyup.enter="onEmailEnter"
              >
                <template #prepend>
                  <v-icon class="uwill-primary">
                    fa-light fa-envelope
                  </v-icon>
                </template>
              </v-text-field>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  id="login"
                  class="uwill-primary-button active shadowed ml-4"
                  size="large"
                  type="button"
                  :loading="loading"
                  :disabled="loading"
                  @click="checkMethod()"
                  >Next</v-btn
                >
              </v-card-actions>
            </v-card>

            <v-card v-if="passwordStage" flat>
              <v-form
                ref="form"
                v-model="valid"
                lazy-validation
                @submit.prevent="login"
              >
                <v-text-field
                  id="password"
                  ref="password_field"
                  v-model="password"
                  label="Password"
                  class="ada-red login-fields"
                  name="password"
                  prepend-icon="fa-light fa-lock"
                  required
                  :error-messages="passwordErrors"
                  autocomplete="password"
                  :type="showPassword ? 'text' : 'password'"
                  @update:model-value="v$.password.$touch()"
                  @blur="v$.password.$touch()"
                  @click:append="showPassword = !showPassword"
                  @keyup.enter="login"
                >
                  <template #append>
                    <button
                      class="mr-1"
                      type="button"
                      :aria-label="'Password visible: ' + showPassword"
                    >
                      <v-icon
                        :aria-label="
                          showPassword ? 'Hide password' : 'Show password'
                        "
                        @click="showPassword = !showPassword"
                      >
                        fa-regular
                        {{ showPassword ? "fa-eye" : "fa-eye-slash" }}
                      </v-icon>
                    </button>
                  </template>
                </v-text-field>
                <v-alert
                  v-if="errors"
                  class="ada-red"
                  type="error"
                  role="alert"
                >
                  <div v-for="(value, name) in errors" :key="name">
                    {{ name }} {{ value }}
                  </div>
                </v-alert>
                <v-card-actions>
                  <a
                    href="#"
                    role="button"
                    @click.prevent="passwordStage = false"
                  >
                    Back
                  </a>
                  <v-spacer />
                  <a href="/forgotpassword" role="button">
                    Forgot Password
                  </a>
                  <v-btn
                    id="login"
                    class="uwill-primary-button shadowed ml-4"
                    selected-class="active"
                    size="large"
                    type="submit"
                    @click="login()"
                    >Log In</v-btn
                  >
                </v-card-actions>
              </v-form>
            </v-card>
          </div>
          <div v-else class="details-content">
            <v-form @submit.prevent="login">
              <v-card flat>
                <div class="text-caption mb-3">
                  Please check <strong>{{ email }}</strong> for an email from
                  Uwill with a One-Time use code.
                </div>
                <v-text-field
                  id="code"
                  ref="code"
                  v-model="code"
                  label="One-Time Code"
                  name="code"
                  prepend-icon="fa-light fa-lock-hashtag"
                  type="text"
                  required
                  @blur="code = code.trim()"
                  @keyup.enter="checkCode"
                />
                <v-alert
                  v-if="errors"
                  class="ada-red"
                  type="error"
                  role="alert"
                >
                  <div v-for="(value, name) in errors" :key="name">
                    {{ name }} {{ value }}
                  </div>
                </v-alert>
                <v-card-actions>
                  <a
                    href="#"
                    tabindex="0"
                    role="button"
                    @click.prevent="
                      passwordStage = true;
                      use_code = false;
                    "
                  >
                    Back
                  </a>
                  <v-spacer />
                  <v-btn
                    id="login_code"
                    class="uwill-primary-button shadowed ml-4"
                    size="large"
                    type="button"
                    :loading="loading"
                    :disabled="loading"
                    @click="checkCode()"
                    >Login</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-form>
          </div>
        </div>
      </span>
      <v-container class="text-center mt-4">
        Don't have an account yet?
        <p>
          <router-link id="register" to="/">
            Register Here
          </router-link>
        </p>
      </v-container>
      <v-container v-if="passwordStage && !use_code" class="text-center mt-1">
        <a @click="sendCode()">
          Email me a <strong role="button">One-Time Login Code</strong>
        </a>
        <div class="text-caption">(instead of using your password)</div>
      </v-container>
    </v-col>
  </v-row>
</template>

<script>
import axios from "axios";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, email } from "@vuelidate/validators";

export default {
  name: "Login",
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      email: { required, email },
      password: { required, minLength: minLength(8) }
    };
  },
  data: () => ({
    valid: true,
    email: "",
    password: "",
    errors: null,
    showPassword: false,
    passwordStage: false,
    loading: false,
    use_code: false,
    gso_mode: false,
    code: "",
    sessionTimedOut: false,
    images: {
      jaceyUwill: require("@/assets/images/jacey_uwill.png")
    }
  }),
  computed: {
    emailErrors() {
      const errors = [];
      if (!this.v$.email.$dirty) return errors;
      if (this.v$.email.$model == undefined) {
        errors.push("Email is required");
      } else if (this.v$.email.$invalid && this.email.indexOf("/i/") == -1) {
        errors.push("Must be valid email");
      }
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.v$.password.$dirty) return errors;
      !this.v$.password.minLength &&
        errors.push("Password must be at least 8 characters long");
      !this.v$.password.required && errors.push("Password is required.");
      return errors;
    },
    slashes() {
      return this.email.indexOf("/i/") != -1;
    }
  },
  watch: {
    email(val) {
      this.email = val.toLowerCase().trim();
    }
  },
  beforeCreate() {
    this.$store.commit("DASHBOARD_FALSE");
  },
  mounted: function() {
    window.addEventListener("keyup", this.enterListener);
    if (this.$store.getters.confirmationToken != undefined) {
      window.Bus.$emit("flash-message", { text: "Please login to continue" });
    }

    // Always bust cache on this page
    localStorage.removeItem("uwillFieldvaluesCacheTimestamp");
    localStorage.removeItem("uwillFieldvalues");
  },
  beforeUnmount: function(){
    window.removeEventListener("keyup", this.enterListener);
  },
  methods: {
    enterListener(event){
      if (event.key == "Enter") {
        if (!self.passwordStage) {
          this.v$.email.$touch();
          if (self.emailErrors && self.emailErrors.length == 0) {
            self.checkMethod();
          }
        } else {
          self.login();
        }
      }
      // Couldn't find a better way to focus nested Vuetify component (transitioning to Vue 3), but it's simple and it works
      // const emailInput = document.getElementById("email");
      // if (emailInput) emailInput.focus();
      if (localStorage.getItem("uwill_session_timed_out")) {
        this.$amplitude.getInstance().logEvent("Session Timed Out");
        this.sessionTimedOut = true;
      }
    },
    checkMethod: function() {
      if (this.emailErrors.length == 0) {
        this.loading = true;
        axios({
          method: "post",
          url: "/login/lookup",
          data: {
            email: this.email
          }
        }).then(resp => {
          if (resp.data.idp_url) {
            window.location = resp.data.idp_url;
          } else if (resp.data.gso_mode) {
            this.gso_mode = true;
          } else {
            this.passwordStage = true;
            this.loading = false;
            setTimeout(function() {
              const passwordInput = document.getElementById("password");
              if (passwordInput) passwordInput.focus();
            }, 500);
          }
        });
      } else {
        window.Bus.$emit("flash-message", {
          text: this.emailErrors.join(", ")
        });
      }
    },
    sendCode: function() {
      axios({
        method: "post",
        url: "/login/code",
        data: {
          email: this.email
        }
      }).then(resp => {
        this.use_code = true;
        if (resp.data) {
          this.errors = resp.data.errors;
          this.loading = false;
        } else {
          setTimeout(function() {
            const codeInput = document.getElementById("code");
            if (codeInput) codeInput.focus();
          }, 500);
        }
      });
    },
    checkCode: function() {
      if (this.code.length != 6) {
        window.Bus.$emit("flash-message", {
          text:
            "Please enter a valid code (which was emailed to " +
            this.email +
            ")"
        });
      } else {
        this.loading = true;
        this.$store
          .dispatch("login", { email: this.email, code: this.code })
          .then(() => {
            this.$router.push("/").catch(() => {});
          })
          .catch(err => {
            this.errors = err;
            this.loading = false;
          });
      }
    },
    login: function() {
      if (this.awaitingLoginToken) return;

      this.awaitingLoginToken = true;

      if (this.sessionTimedOut) {
        localStorage.removeItem("uwill_session_timed_out");
      }
      this.v$.$touch();
      if (!this.v$.$invalid || this.slashes) {
        this.v$.$reset();
        let email = this.email;
        let password = this.password;
        this.$store
          .dispatch("login", { email, password })
          .then(() => {
            let target = this.$store.getters.requestToView;
            let redirectPath = this.$route.query.redirect || "/";
            if (target == undefined) {
              this.$router.push(redirectPath).catch(() => {});
            } else {
              this.$store.commit("REQUEST_TO_VIEW",undefined);
              this.$router.push(`/dashboard?tab=wellness&view=${target}`);
            }
          })
          .catch(err => {
            this.errors = err;
            this.awaitingLoginToken = false;
          });
      }
    },
    onEmailEnter() {
      this.v$.email.$touch();
      if (this.emailErrors.length === 0) {
        this.checkMethod();
      }
    }
  }
};
</script>

<style scoped>
.login-cover {
  background-image: url("@/assets/images/studentcover.png");
  background-size: cover;
}
.login-row {
  height: 105%;
}
.mobile {
  height: 50px;
}
.mobile .login-cover {
  background-image: none;
}
.login-row.mobile {
  background-image: url("@/assets/images/studentcover.png");
  background-size: cover;
  height: 550px;
}
.ulogo {
  position: relative;
  top: 30px;
  left: 30px;
  width: 250px;
}
h1.login-header {
  margin-top: 5%;
  margin-bottom: 10px;
  text-align: center;
  font-weight: 300;
}
h1.login-header.mobile {
  font-size: 28px;
  line-height: 28px;
  margin-bottom: 0px;
}
</style>
